import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import EditableResumePreview from '../components/EditableResumePreview';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Upload, FileText, Building2, Briefcase, Crown, Code2, GraduationCap,
  ArrowLeft, ArrowRight, Download, Loader2, Check, Eye, Sparkles,
  Minimize2, Target, Star, Shield, Edit3, Lock, Clock, LogIn, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, getAuthToken } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Template icons mapping
const TEMPLATE_ICONS = {
  corporate: Building2,
  modern: Briefcase,
  executive: Crown,
  minimalist: Minimize2,
  tech: Code2,
  ats: Target,
};

const CATEGORIES = ['All', 'Professional', 'Modern', 'Creative', 'Technical', 'Executive', 'Industry'];

export const ConvertPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [result, setResult] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [fromRefine, setFromRefine] = useState(false);
  const [fromMatchScore, setFromMatchScore] = useState(false); // Track if coming from Match Score page
  const [matchScoreData, setMatchScoreData] = useState(null); // Store match analysis for optimization
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pendingPaymentPlan, setPendingPaymentPlan] = useState(null); // Track which plan user selected before login
  const [optimizationApplied, setOptimizationApplied] = useState(false); // Track if job match optimization was applied
  const [appliedOptimizations, setAppliedOptimizations] = useState(null); // Store what was optimized for display

  // Check if coming from Refine page OR returning after login/payment
  useEffect(() => {
    const fromRefinePage = sessionStorage.getItem('refinedResumeId');
    const premiumJustActivated = sessionStorage.getItem('premiumJustActivated');
    const uploadedResumeId = sessionStorage.getItem('uploadedResumeId') || localStorage.getItem('uploadedResumeId');
    const savedTemplateId = sessionStorage.getItem('selectedTemplateId') || localStorage.getItem('selectedTemplateId');
    const pendingPlan = sessionStorage.getItem('pendingPaymentPlan') || localStorage.getItem('pendingPaymentPlan');
    const savedStep = sessionStorage.getItem('convertStep');
    const singleDownloadGranted = sessionStorage.getItem('singleDownloadGranted');
    
    // Check for match score flow early
    const fromMatchScorePage = sessionStorage.getItem('fromMatchScore');
    const matchScoreResumeId = sessionStorage.getItem('matchScoreResumeId');
    const storedMatchScoreData = sessionStorage.getItem('matchScoreData');
    
    console.log('ConvertPage mount - state:', { fromRefinePage, uploadedResumeId, savedTemplateId, pendingPlan, premiumJustActivated, savedStep, singleDownloadGranted, fromMatchScorePage, matchScoreResumeId });
    
    // Handle return after successful single download payment (₹29)
    if (singleDownloadGranted === 'true' && uploadedResumeId && savedTemplateId) {
      console.log('Single download granted - triggering download');
      sessionStorage.removeItem('singleDownloadGranted');
      setResumeId(uploadedResumeId);
      setSelectedTemplate(savedTemplateId);
      setStep(4);
      
      // Trigger download after state is set
      setTimeout(async () => {
        await performDownloadDirect(uploadedResumeId, savedTemplateId, 'pdf', false);
      }, 500);
      return;
    }
    
    // Handle return after login - user selected a plan and logged in
    if (user && uploadedResumeId && savedTemplateId && pendingPlan) {
      console.log('Returning after login with pending payment plan:', pendingPlan);
      setResumeId(uploadedResumeId);
      setSelectedTemplate(savedTemplateId);
      setStep(4);
      setPendingPaymentPlan(pendingPlan);
      
      // Clear the pending plan state
      sessionStorage.removeItem('pendingPaymentPlan');
      sessionStorage.removeItem('convertStep');
      localStorage.removeItem('pendingPaymentPlan');
      
      // Trigger payment after state is set - pass the IDs directly
      setTimeout(() => {
        handlePaymentWithIds(pendingPlan, uploadedResumeId, savedTemplateId);
      }, 500);
      return;
    }
    
    // Handle payment return - premium was just activated (₹79 plan)
    if (premiumJustActivated === 'true') {
      sessionStorage.removeItem('premiumJustActivated');
      console.log('Premium just activated, restoring session');
      if (uploadedResumeId) {
        setResumeId(uploadedResumeId);
        if (savedTemplateId) {
          setSelectedTemplate(savedTemplateId);
          setStep(4); // Go to preview step for download
          // Auto-download for premium
          setTimeout(async () => {
            await performDownloadDirect(uploadedResumeId, savedTemplateId, 'pdf', false);
          }, 500);
        } else {
          setStep(2); // Go to template selection
        }
        return;
      }
    }
    
    // Coming from Refine page - user clicked "Choose Template" after refining
    if (fromRefinePage) {
      console.log('Restoring from refinedResumeId:', fromRefinePage);
      setResumeId(fromRefinePage);
      setFromRefine(true);
      setStep(2);
      // Don't remove yet - will be cleared after successful download or navigation away
      // sessionStorage.removeItem('refinedResumeId');
      return;
    }
    
    // Coming from Match Score page - user already uploaded resume, skip to template selection
    if (fromMatchScorePage === 'true' && matchScoreResumeId) {
      console.log('Coming from Match Score page with resumeId:', matchScoreResumeId);
      setResumeId(matchScoreResumeId);
      setFromMatchScore(true);
      setStep(2); // Go directly to template selection
      
      // Parse and store match score data for optimization
      if (storedMatchScoreData) {
        try {
          const parsedMatchData = JSON.parse(storedMatchScoreData);
          setMatchScoreData(parsedMatchData);
          console.log('Match score data loaded:', parsedMatchData);
        } catch (e) {
          console.error('Failed to parse match score data:', e);
        }
      }
      
      // Clear the flag but keep resumeId and match data for the flow
      sessionStorage.removeItem('fromMatchScore');
      return;
    }
    
    // Returning after login but no pending payment - restore state and show paywall
    // Only do this if user was in an ACTIVE payment flow (has downloadId indicating intent to pay)
    const downloadIdExists = sessionStorage.getItem('downloadId') || localStorage.getItem('downloadId');
    if (user && uploadedResumeId && savedTemplateId && !pendingPlan && downloadIdExists) {
      console.log('Returning after login with active download flow - restoring state and re-converting');
      setResumeId(uploadedResumeId);
      setSelectedTemplate(savedTemplateId);
      setStep(parseInt(savedStep) || 4);
      
      // Re-convert the resume to get the HTML content
      reconvertResume(uploadedResumeId, savedTemplateId);
      
      // Show paywall so user can retry payment
      setTimeout(() => setShowPaywallModal(true), 500);
      
      // Clean up stored values (keep resume and template for download)
      sessionStorage.removeItem('convertStep');
      return;
    }
    
    // Fresh visit to /convert - ALWAYS start at step 1 (upload)
    // This ensures users always see the upload option when clicking "Convert to Template" from home
    // BUT don't clear if coming from Match Score page
    const isFromMatchScore = sessionStorage.getItem('fromMatchScore') === 'true' || sessionStorage.getItem('matchScoreResumeId');
    if (!premiumJustActivated && !fromRefinePage && !pendingPlan && !downloadIdExists && !isFromMatchScore) {
      sessionStorage.removeItem('uploadedResumeId');
      localStorage.removeItem('uploadedResumeId');
      sessionStorage.removeItem('selectedTemplateId');
      localStorage.removeItem('selectedTemplateId');
      sessionStorage.removeItem('downloadId');
      localStorage.removeItem('downloadId');
      sessionStorage.removeItem('downloadTemplateId');
      localStorage.removeItem('downloadTemplateId');
      sessionStorage.removeItem('convertStep');
      sessionStorage.removeItem('matchScoreResumeId');
      sessionStorage.removeItem('matchScoreData');
      setStep(1); // Explicitly set to upload step
      console.log('Fresh visit - starting at step 1 (upload)');
    }
    
    // Store current path for upgrade return
    sessionStorage.setItem('upgradeReturnPath', '/convert');
  }, [user]);

  // Fetch templates and premium status on mount and when returning from upgrade
  useEffect(() => {
    fetchTemplates();
    fetchPremiumStatus();
    
    // Also refresh when navigating back to this page
    const handleFocus = () => {
      fetchPremiumStatus();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Refresh premium status when page becomes visible (e.g., returning from payment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchPremiumStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Handle back navigation based on context
  const handleBack = () => {
    if (step === 1) {
      // At upload step, go to home
      navigate('/home');
    } else if (step === 2) {
      if (fromRefine) {
        // If came from Refine, go back to Refine page
        navigate('/refine');
      } else {
        // Otherwise go to upload step
        setStep(1);
      }
    } else if (step === 3) {
      // During processing, go back to template selection
      setStep(2);
    } else if (step === 4) {
      // After download/preview, go back to template selection (resume already uploaded)
      setStep(2);
      setSelectedTemplate(null);
      setResult(null);
      setEditableData(null);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        // No token = not logged in, no need to fetch
        setPremiumStatus({ is_premium: false });
        return;
      }
      const response = await authFetch('/users/premium-status');
      if (response.ok) {
        const data = await response.json();
        setPremiumStatus(data);
        // Also check if premium just expired and show message
        if (data.premium_just_expired) {
          toast.info('Your premium access has expired. Upgrade again for continued access.');
        }
      } else {
        // Not logged in or error - set default status
        setPremiumStatus({ is_premium: false });
      }
    } catch (error) {
      console.error('Failed to fetch premium status:', error);
      setPremiumStatus({ is_premium: false });
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/resumes/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const filteredTemplates = selectedCategory === 'All' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);
  const isPremiumTemplate = selectedTemplateData?.isPremium;
  const userIsPremium = premiumStatus?.is_premium;

  const handleFileUpload = useCallback(async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    const fileExt = uploadedFile.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx'].includes(fileExt)) {
      toast.error('Only PDF and DOCX files are supported');
      return;
    }

    setFile(uploadedFile);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      
      // Get token for Authorization header
      const token = getAuthToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/resumes/upload`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setResumeId(data.resume_id);
        // IMPORTANT: Save to sessionStorage so resume persists after payment redirect
        sessionStorage.setItem('uploadedResumeId', data.resume_id);
        toast.success('Resume uploaded successfully');
        setStep(2);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload resume');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConvert = async () => {
    if (!resumeId || !selectedTemplate) return;
    
    // Allow conversion for all templates - paywall is at download
    setLoading(true);
    setStep(3);

    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // If coming from Match Score, include the optimization data
      const requestBody = { 
        resume_id: resumeId, 
        template: selectedTemplate 
      };
      
      // Include match score data for AI optimization if available
      if (fromMatchScore && matchScoreData) {
        requestBody.apply_job_match_optimization = true;
        requestBody.job_match_data = matchScoreData;
        console.log('Applying job match optimization with data:', matchScoreData);
      }
      
      const response = await fetch(`${API}/resumes/convert`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (response.ok) {
        setResult(data);
        // Use the transformed editable_data from backend
        setEditableData(data.editable_data || null);
        
        // Track optimization for UI display
        if (data.job_match_optimized) {
          setOptimizationApplied(true);
          // Store what was optimized for showing to user
          setAppliedOptimizations({
            keywords_added: matchScoreData?.missing_keywords?.slice(0, 8) || [],
            improvements_made: matchScoreData?.weak_points?.length || 0,
            suggestions_applied: matchScoreData?.suggestions?.length || 0
          });
        }
        
        // Clear match score session data after successful conversion
        if (fromMatchScore) {
          sessionStorage.removeItem('matchScoreResumeId');
          sessionStorage.removeItem('matchScoreData');
          toast.success('Resume optimized and converted successfully!');
        } else {
          toast.success('Resume converted successfully');
        }
        setStep(4);
      } else {
        console.error('Convert failed:', data);
        toast.error(data.detail || 'Conversion failed');
        setStep(2);
      }
    } catch (error) {
      console.error('Convert error:', error);
      toast.error('Failed to convert resume. Please try uploading again.');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  // Re-convert resume when returning from login/payment (restore state)
  const reconvertResume = async (resId, templateId) => {
    if (!resId || !templateId) return;
    
    setLoading(true);
    
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/convert`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ resume_id: resId, template: templateId }),
      });

      const data = await response.json();
      if (response.ok) {
        setResult(data);
        setEditableData(data.editable_data || null);
        console.log('Resume re-converted successfully after login return');
      } else {
        console.error('Re-convert failed:', data);
        toast.error('Failed to reload resume. Please try again.');
        setStep(2); // Go back to template selection
      }
    } catch (error) {
      console.error('Re-convert error:', error);
      toast.error('Failed to reload resume.');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = (newData) => {
    setEditableData(newData);
  };

  const handleSaveEdits = async (savedData) => {
    setEditableData(savedData);
    setLoading(true);
    
    try {
      // Transform data back to backend format for re-rendering
      const backendData = {
        full_name: savedData.full_name,
        professional_title: savedData.professional_title,
        email: savedData.email,
        phone: savedData.phone,
        location: savedData.location,
        linkedin: savedData.linkedin,
        summary: savedData.summary,
        skills: (savedData.skills?.length > 0),
        skills_list: savedData.skills || [],
        experience: (savedData.experience?.length > 0),
        experience_list: (savedData.experience || []).map(exp => ({
          job_title: exp.job_title,
          company: exp.company,
          dates: exp.dates,
          achievements: exp.achievements || []
        })),
        education: (savedData.education?.length > 0),
        education_list: (savedData.education || []).map(edu => ({
          degree: edu.degree,
          university: edu.university,
          year: edu.year
        })),
        certifications: (savedData.certifications?.length > 0),
        cert_list: savedData.certifications || []
      };

      // Re-render template with edited data
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/resumes/template/rerender`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          resume_id: resumeId, 
          template: selectedTemplate,
          edited_data: backendData
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(prev => ({
          ...prev,
          extracted_data: data.extracted_data,
          converted_html: data.converted_html
        }));
        setShowEditPanel(false);
        toast.success('Preview updated with your changes!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to update preview');
      }
    } catch (error) {
      toast.error('Failed to update preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    if (!resumeId || !selectedTemplate) return;
    
    // SCENARIO 1 & 3: FREE TEMPLATE - download with watermark for everyone
    if (!isPremiumTemplate) {
      await performDownload(format, true); // Always with watermark for free templates
      return;
    }
    
    // SCENARIO 2: PREMIUM USER downloading PREMIUM TEMPLATE - direct download without watermark
    if (userIsPremium) {
      await performDownload(format, false); // No watermark for premium users
      return;
    }
    
    // SCENARIO 4: NON-PREMIUM USER trying to download PREMIUM TEMPLATE
    // Show paywall modal - it will handle login vs payment based on user state
    setShowPaywallModal(true);
  };

  // Prepare resume for payment - saves to backend and gets download_id
  const prepareForDownload = async () => {
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Use the correct endpoint: /api/resumes/save-for-download
      const response = await fetch(`${API}/resumes/save-for-download`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          resume_id: resumeId,
          template_id: selectedTemplate,
          source_page: 'convert'
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.download_id;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to prepare for download');
      }
    } catch (error) {
      console.error('Prepare for download error:', error);
      throw error;
    }
  };

  // Handle login redirect with plan selection
  const handleLoginWithPlan = async (plan) => {
    try {
      // First, clear any old download_id (it might be for a different template)
      sessionStorage.removeItem('downloadId');
      localStorage.removeItem('downloadId');
      
      // Prepare the resume for download with the CURRENT template (get download_id)
      setPaymentProcessing(true);
      const downloadId = await prepareForDownload();
      
      // Store all state for after login - including the specific template
      sessionStorage.setItem('uploadedResumeId', resumeId);
      sessionStorage.setItem('selectedTemplateId', selectedTemplate);
      sessionStorage.setItem('pendingPaymentPlan', plan);
      sessionStorage.setItem('downloadId', downloadId);
      sessionStorage.setItem('downloadTemplateId', selectedTemplate); // Store which template this download_id is for
      sessionStorage.setItem('redirectAfterLogin', '/convert');
      sessionStorage.setItem('convertStep', '4');
      localStorage.setItem('uploadedResumeId', resumeId);
      localStorage.setItem('selectedTemplateId', selectedTemplate);
      localStorage.setItem('pendingPaymentPlan', plan);
      localStorage.setItem('downloadId', downloadId);
      localStorage.setItem('downloadTemplateId', selectedTemplate);
      localStorage.setItem('redirectAfterLogin', '/convert');
      
      setShowPaywallModal(false);
      setPaymentProcessing(false);
      navigate('/login');
    } catch (error) {
      toast.error('Failed to prepare download. Please try again.');
      setPaymentProcessing(false);
    }
  };

  // Direct download with explicit IDs (used after payment/login redirect)
  const performDownloadDirect = async (downloadResumeId, downloadTemplateId, format, withWatermark = true) => {
    setLoading(true);
    console.log('performDownloadDirect called with:', { downloadResumeId, downloadTemplateId, format, withWatermark });

    try {
      const token = getAuthToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${API}/resumes/download/${downloadResumeId}?format=${format}&content_type=template_${downloadTemplateId}&with_watermark=${withWatermark}`,
        { credentials: 'include', headers }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resume_${downloadTemplateId}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Downloaded as ${format.toUpperCase()}`);
        setShowPaywallModal(false);
        
        // Clear ALL stored IDs after successful download - IMPORTANT for fresh starts
        sessionStorage.removeItem('uploadedResumeId');
        sessionStorage.removeItem('selectedTemplateId');
        sessionStorage.removeItem('refinedResumeId');
        sessionStorage.removeItem('downloadId');
        sessionStorage.removeItem('downloadTemplateId');
        sessionStorage.removeItem('convertStep');
        sessionStorage.removeItem('pendingPaymentPlan');
        sessionStorage.removeItem('singleDownloadGranted');
        sessionStorage.removeItem('premiumJustActivated');
        localStorage.removeItem('uploadedResumeId');
        localStorage.removeItem('selectedTemplateId');
        localStorage.removeItem('downloadId');
        localStorage.removeItem('downloadTemplateId');
        localStorage.removeItem('pendingPaymentPlan');
      } else {
        const data = await response.json();
        console.error('Download failed:', data);
        if (response.status === 403) {
          toast.error(data.detail || 'Download failed. Please try again.');
        } else {
          toast.error(data.detail || 'Download failed');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download');
    } finally {
      setLoading(false);
    }
  };

  const performDownload = async (format, withWatermark = true) => {
    await performDownloadDirect(resumeId, selectedTemplate, format, withWatermark);
  };

  // Payment handler with explicit IDs (for after login redirect)
  const handlePaymentWithIds = async (plan, payResumeId, payTemplateId) => {
    setPaymentProcessing(true);
    
    try {
      const token = getAuthToken();
      
      // CRITICAL FIX: Always create a NEW download_id for the current template
      // Don't reuse old download_id - it might be for a different template!
      // Clear old download_id first
      sessionStorage.removeItem('downloadId');
      localStorage.removeItem('downloadId');
      
      // Always prepare for download with the CURRENT template
      const prepareResponse = await fetch(`${API}/resumes/save-for-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          resume_id: payResumeId,
          template_id: payTemplateId,
          source_page: 'convert'
        })
      });
      
      if (!prepareResponse.ok) {
        const errData = await prepareResponse.json();
        throw new Error(errData.detail || 'Failed to prepare download');
      }
      
      const prepareData = await prepareResponse.json();
      const downloadId = prepareData.download_id;
      
      // Store the NEW download_id for this specific template
      sessionStorage.setItem('downloadId', downloadId);
      sessionStorage.setItem('downloadTemplateId', payTemplateId);
      
      const response = await fetch(`${API}/users/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: plan,
          download_id: downloadId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await response.json();
      
      // Store IDs for use after payment
      sessionStorage.setItem('downloadId', downloadId);
      sessionStorage.setItem('paymentTemplateId', payTemplateId);
      sessionStorage.setItem('paymentPlan', plan);
      
      const options = {
        key: orderData.razorpay_key_id || orderData.key_id,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: 'FixResume24',
        description: plan === 'single' ? 'Single Clean Download (₹29)' : 'Premium Access 24hrs (₹79)',
        order_id: orderData.order_id,
        handler: function (razorpayResponse) {
          // Use synchronous handler, then call async verification
          verifyPaymentAndRedirect(
            razorpayResponse.razorpay_order_id,
            razorpayResponse.razorpay_payment_id,
            razorpayResponse.razorpay_signature
          );
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || ''
        },
        theme: {
          color: '#1e3a5f'
        },
        modal: {
          ondismiss: function() {
            setPaymentProcessing(false);
            setShowPaywallModal(false);
            // Keep the user on step 4 so they can retry download
            setStep(4);
            toast.info('Payment cancelled. You can try again by clicking the download button.');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
      setPaymentProcessing(false);
    }
  };

  // Separate async function for payment verification - REDIRECTS to download page
  const verifyPaymentAndRedirect = async (orderId, paymentId, signature) => {
    try {
      const token = getAuthToken();
      const plan = sessionStorage.getItem('paymentPlan');
      const downloadId = sessionStorage.getItem('downloadId');
      const paymentTemplateId = sessionStorage.getItem('paymentTemplateId');
      
      console.log('Verifying payment:', { orderId, paymentId, plan, downloadId, paymentTemplateId });
      
      const verifyResponse = await fetch(`${API}/users/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
          plan: plan,
          download_id: downloadId
        })
      });

      if (verifyResponse.ok) {
        const result = await verifyResponse.json();
        toast.success('Payment successful!');
        setShowPaywallModal(false);
        
        // Only set premium for ₹79 plan
        if (plan === 'premium') {
          setPremiumStatus({ is_premium: true });
        }
        
        // Clean up session storage
        sessionStorage.removeItem('paymentPlan');
        sessionStorage.removeItem('paymentTemplateId');
        sessionStorage.removeItem('pendingPaymentPlan');
        
        // REDIRECT to download page with download_id
        const finalDownloadId = result.download_id || downloadId;
        if (finalDownloadId) {
          // Use window.location for hard redirect to ensure clean state
          window.location.href = `/download?download_id=${finalDownloadId}`;
        } else {
          toast.error('Download link not available. Please contact support.');
        }
      } else {
        const errorData = await verifyResponse.json();
        console.error('Payment verification failed:', errorData);
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Payment verification failed. Please contact support.');
    }
    setPaymentProcessing(false);
  };

  // Regular payment handler (uses current state)
  const handlePayment = async (plan) => {
    await handlePaymentWithIds(plan, resumeId, selectedTemplate);
  };

  const handleLoginRedirect = () => {
    // Store state before redirecting to login
    sessionStorage.setItem('uploadedResumeId', resumeId);
    sessionStorage.setItem('selectedTemplateId', selectedTemplate);
    sessionStorage.setItem('redirectAfterLogin', '/convert');
    sessionStorage.setItem('convertStep', String(step));
    localStorage.setItem('uploadedResumeId', resumeId);
    localStorage.setItem('selectedTemplateId', selectedTemplate);
    localStorage.setItem('redirectAfterLogin', '/convert');
    
    setShowLoginModal(false);
    navigate('/login');
  };

  // Template Preview Card Component
  const TemplatePreviewCard = ({ template, isSelected, onClick }) => {
    const iframeRef = useRef(null);
    const Icon = TEMPLATE_ICONS[template.id] || Star;
    
    return (
      <Card 
        className={`cursor-pointer transition-all overflow-hidden ${
          isSelected 
            ? 'ring-2 ring-primary ring-offset-2 shadow-lg' 
            : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
        }`}
        onClick={onClick}
        data-testid={`template-${template.id}`}
      >
        {/* Preview Frame */}
        <div className="relative aspect-[3/4] overflow-hidden bg-white border-b border-slate-100">
          <div className="absolute inset-0 transform scale-[0.35] origin-top-left w-[285%] h-[285%]">
            <iframe
              ref={iframeRef}
              srcDoc={template.preview_html}
              title={template.name}
              className="w-full h-full border-0 pointer-events-none"
              sandbox="allow-same-origin"
            />
          </div>
          
          {/* ATS Label Badge */}
          {template.ats_label && (
            <Badge 
              className={`absolute top-2 left-2 text-xs z-10 ${
                template.ats_label === 'ATS-Friendly' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-amber-500 text-white'
              }`}
            >
              <Shield className="h-3 w-3 mr-1" />
              {template.ats_label}
            </Badge>
          )}
          
          {/* Premium Badge */}
          {template.isPremium && (
            <Badge className="absolute top-2 right-2 bg-accent text-white text-xs z-10">
              <Crown className="h-3 w-3 mr-1" />
              PRO
            </Badge>
          )}
          
          {/* Selected Overlay */}
          {isSelected && (
            <div className="absolute inset-0 bg-primary/10 flex items-center justify-center z-10">
              <div className="bg-primary text-white rounded-full p-2">
                <Check className="h-6 w-6" />
              </div>
            </div>
          )}
        </div>
        
        {/* Template Info */}
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
            <h3 className="font-semibold text-sm text-primary">
              {template.name}
            </h3>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            {template.description}
          </p>
          <p className="text-xs text-primary/70 font-medium">
            Best for: {template.best_for}
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={handleBack}
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {step === 1 ? 'Back to Home' : step === 4 ? 'Choose Another Template' : fromRefine && step === 2 ? 'Back to Refine' : 'Back'}
        </Button>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {step} of 4</span>
            <span>{step === 1 ? 'Upload' : step === 2 ? 'Choose Template' : step === 3 ? 'Processing' : 'Preview & Edit'}</span>
          </div>
          <Progress value={(step / 4) * 100} className="h-2" />
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
                Convert to Professional Template
              </h1>
              <p className="text-muted-foreground">
                Upload your resume to transform it into a stunning professional template
              </p>
            </div>

            <Card className="max-w-xl mx-auto border-slate-100 mb-12">
              <CardContent className="p-8">
                <label className="block cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center hover:border-primary hover:bg-slate-50 transition-all">
                    {loading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
                        <p className="text-muted-foreground">Processing your resume...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-medium text-primary mb-2">
                          Drop your resume here
                        </p>
                        <p className="text-sm text-muted-foreground">
                          or click to browse (PDF or DOCX)
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-green-600">
                          <Shield className="h-3.5 w-3.5" />
                          <span>Your resume is processed securely and never shared with third parties.</span>
                        </div>
                      </>
                    )}
                  </div>
                </label>
              </CardContent>
            </Card>

            {/* Template Preview Gallery */}
            <div>
              <h2 className="font-heading text-xl font-semibold text-primary text-center mb-6">
                Choose Resume Template
              </h2>
              
              {loadingTemplates ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {templates.slice(0, 6).map((template) => (
                    <TemplatePreviewCard 
                      key={template.id}
                      template={template}
                      isSelected={false}
                      onClick={() => {}}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Select Template */}
        {step === 2 && (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
                Choose Resume Template
              </h1>
              <p className="text-muted-foreground">
                Select a template that best fits your industry and experience
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              {/* Top Action Bar - Convert Button at Top */}
              <div className="sticky top-20 z-10 bg-white/95 backdrop-blur-sm border rounded-lg p-3 sm:p-4 mb-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div className="flex items-center gap-3">
                    {selectedTemplateData ? (
                      <>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-primary text-sm sm:text-base truncate">{selectedTemplateData.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {isPremiumTemplate ? (
                              <span className="text-amber-600 flex items-center gap-1">
                                <Crown className="h-3 w-3" /> Premium Template
                              </span>
                            ) : (
                              'Free Template'
                            )}
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted-foreground text-sm">Select a template below to continue</p>
                    )}
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button 
                      variant="outline" 
                      onClick={handleBack}
                      data-testid="step2-back-btn"
                      className="flex-1 sm:flex-none text-xs sm:text-sm"
                      size="sm"
                    >
                      <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">{fromRefine ? 'Back to Refine' : 'Back'}</span>
                      <span className="sm:hidden">Back</span>
                    </Button>
                    {/* Always allow convert - paywall is at download */}
                    <Button 
                      onClick={handleConvert}
                      disabled={!selectedTemplate}
                      data-testid="step2-next-btn"
                      className="flex-1 sm:flex-none text-xs sm:text-sm"
                      size="sm"
                    >
                      Convert
                      <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Show refined content banner if coming from Refine page */}
              {fromRefine && (
                <div className="flex items-center gap-3 mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Sparkles className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <span className="font-medium text-green-700">Using your refined resume</span>
                    <p className="text-xs text-green-600">Your improved content will be applied to the selected template</p>
                  </div>
                  <Check className="h-5 w-5 text-green-600" />
                </div>
              )}

              {file && !fromRefine && (
                <div className="flex items-center gap-3 mb-6 p-4 bg-slate-50 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="font-medium text-primary">{file.name}</span>
                  <Check className="h-4 w-4 text-green-600 ml-auto" />
                </div>
              )}

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-8 justify-center">
                {CATEGORIES.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 text-muted-foreground hover:bg-slate-200'
                    }`}
                    data-testid={`category-${category.toLowerCase()}`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Templates Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredTemplates.map((template) => (
                  <TemplatePreviewCard 
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplate === template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {step === 3 && (
          <div className="animate-fade-in text-center py-16">
            <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-primary mb-2">
              Converting Your Resume
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              Extracting information and formatting into {selectedTemplateData?.name}...
            </p>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>✓ Parsing resume content</p>
              <p>✓ Extracting structured data</p>
              <p>→ Applying template formatting...</p>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Download with Editable Panel */}
        {step === 4 && result && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
                Your Resume is Ready!
              </h1>
              <p className="text-muted-foreground">
                {result.template_name} template applied successfully. Click &quot;Edit Resume&quot; to make changes.
              </p>
            </div>

            {/* Optimization Summary - Show what was changed */}
            {optimizationApplied && appliedOptimizations && (
              <Card className="max-w-4xl mx-auto mb-8 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        Resume Optimized for Your Target Job!
                      </h3>
                      <p className="text-sm text-green-700 mb-3">
                        We&apos;ve automatically enhanced your resume based on the job description analysis:
                      </p>
                      <div className="space-y-2">
                        {appliedOptimizations.keywords_added?.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-medium text-green-800">Keywords Added:</span>
                            {appliedOptimizations.keywords_added.map((kw, i) => (
                              <Badge key={i} className="bg-green-100 text-green-700 text-xs">
                                + {kw}
                              </Badge>
                            ))}
                            {appliedOptimizations.keywords_added.length >= 8 && (
                              <span className="text-xs text-green-600">+ more</span>
                            )}
                          </div>
                        )}
                        {appliedOptimizations.improvements_made > 0 && (
                          <p className="text-xs text-green-700">
                            <Check className="h-3 w-3 inline mr-1" />
                            {appliedOptimizations.improvements_made} weak sections improved with better wording
                          </p>
                        )}
                        {appliedOptimizations.suggestions_applied > 0 && (
                          <p className="text-xs text-green-700">
                            <Check className="h-3 w-3 inline mr-1" />
                            Summary and skills optimized for ATS compatibility
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6">
              {/* Left: Edit Panel */}
              <div>
                {editableData && (
                  <EditableResumePreview
                    resumeId={resumeId}
                    initialData={editableData}
                    onSave={handleSaveEdits}
                    onDataChange={handleDataChange}
                    source="template"
                    templateId={selectedTemplate}
                  />
                )}
              </div>

              {/* Right: Template Preview */}
              <div>
                <Card className="border-slate-100">
                  <CardHeader className="pb-3 border-b">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Template Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="bg-white">
                      <iframe
                        srcDoc={result.converted_html}
                        title="Resume Preview"
                        className="w-full border-0"
                        style={{ height: '600px' }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Download Actions */}
            <Card className="max-w-4xl mx-auto border-primary/20 bg-primary/5 mt-8">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-center sm:text-left">
                    <h3 className="font-heading font-semibold text-primary mb-1 text-sm sm:text-base">
                      Download Your Resume
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Get your professionally formatted resume as PDF
                    </p>
                  </div>
                  <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                    <Button 
                      variant="outline"
                      onClick={() => { setStep(2); setResult(null); setEditableData(null); setShowEditPanel(false); }}
                      data-testid="change-template-btn"
                      className="flex-1 sm:flex-none text-xs sm:text-sm"
                      size="sm"
                    >
                      <Sparkles className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Try Different</span>
                      <span className="sm:hidden">Change</span>
                    </Button>
                    <Button
                      onClick={() => handleDownload('pdf')}
                      disabled={loading}
                      data-testid="download-pdf-btn"
                      className="flex-1 sm:flex-none text-xs sm:text-sm"
                      size="sm"
                    >
                      {loading ? (
                        <Loader2 className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                      ) : (
                        <Download className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Sign In Required</DialogTitle>
            <DialogDescription className="text-center">
              Please sign in to download your resume
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button onClick={handleLoginRedirect} className="w-full">
              <LogIn className="mr-2 h-4 w-4" />
              Sign In with Google
            </Button>
            <Button variant="outline" onClick={() => setShowLoginModal(false)}>
              Continue Editing
            </Button>
          </div>
          <p className="text-xs text-center text-muted-foreground">
            Your progress is saved. You can continue after signing in.
          </p>
        </DialogContent>
      </Dialog>

      {/* Paywall Modal - Only for PREMIUM templates */}
      <Dialog open={showPaywallModal} onOpenChange={setShowPaywallModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-center">
              <Crown className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <span className="text-xl">Download Options</span>
            </DialogTitle>
            <DialogDescription className="text-center">
              Premium template selected - Choose your plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {/* ₹29 Option - Clean download for current template */}
            <Card 
              className="cursor-pointer hover:border-primary transition-colors"
              data-testid="payment-option-29"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">Clean Download</p>
                    <p className="text-sm text-muted-foreground">No watermark, current template only</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">₹29</p>
                    <p className="text-xs text-muted-foreground">one-time</p>
                  </div>
                </div>
                {user ? (
                  <Button 
                    className="w-full" 
                    onClick={() => !paymentProcessing && handlePayment('single')}
                    disabled={paymentProcessing}
                  >
                    {paymentProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Pay ₹29
                  </Button>
                ) : (
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => handleLoginWithPlan('single')}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In to Pay ₹29
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* ₹79 Option - Full premium access */}
            <Card 
              className="cursor-pointer border-primary bg-primary/5 hover:bg-primary/10 transition-colors relative"
              data-testid="payment-option-79"
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-accent text-white">Best Value</Badge>
              </div>
              <CardContent className="p-4 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      Premium Access
                    </p>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-0.5">
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        All 29 templates
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Unlimited downloads
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        24-hour access
                      </li>
                    </ul>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">₹79</p>
                    <p className="text-xs text-muted-foreground">24 hours</p>
                  </div>
                </div>
                {user ? (
                  <Button 
                    className="w-full bg-primary" 
                    onClick={() => !paymentProcessing && handlePayment('premium')}
                    disabled={paymentProcessing}
                  >
                    {paymentProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Pay ₹79
                  </Button>
                ) : (
                  <Button 
                    className="w-full"
                    onClick={() => handleLoginWithPlan('premium')}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In to Pay ₹79
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Secure payment via Razorpay. No auto-renewal.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConvertPage;
